import { push } from 'react-router-redux'
import uniq from 'lodash.uniq'
import mapValues from 'lodash.mapvalues'

import { showBanner } from './banners'
import { fetch, schemas } from '../utils/fetch'
import { getFavouriteImagesAlbum } from '../utils/images'
import {
  ENDPOINT_ALBUM,
  ENDPOINT_NEW_ALBUM,
  ENDPOINT_SET_FAVOURITE_ALBUM,
  ENDPOINT_SHARE_IMAGES
} from '../constants'

const FETCH_ALBUM_REQUEST = 'blayzr/album/REQUEST'
const FETCH_ALBUM_SUCCESS = 'blayzr/album/SUCCESS'
const FETCH_ALBUM_FAILURE = 'blayzr/album/FAILURE'

const SAVE_ALBUM_REQUEST = 'blayzr/album/SAVE_REQUEST'
export const SAVE_ALBUM_SUCCESS = 'blayzr/album/SAVE_SUCCESS'
const SAVE_ALBUM_FAILURE = 'blayzr/album/SAVE_FAILURE'

export const DELETE_ALBUM = 'blayzr/album/DELETE'

export const SET_FAVOURITE_ALBUM = 'blayzr/album/SET_FAVOURITE'
export const REMOVE_FAVOURITE_ALBUM = 'blayzr/album/REMOVE_FAVOURITE'

const NEW_ALBUM_ID = '__NEW__'

// No reducer needed because everything is
// handled by the entities reducer

// The entities get merged by the entities reducer
// so this util lets us easily create an action that
// updates album data
function createAction (type, id, data) {
  return {
    type,
    payload: { entities: { albums: {
      [id]: data
    } } },
    meta: { id }
  }
}

function fetchAlbumRequest (id) {
  return createAction(FETCH_ALBUM_REQUEST, id, {
    fetching: true,
    fetchError: null
  })
}

function fetchAlbumSuccess (id, data) {
  return createAction(FETCH_ALBUM_SUCCESS, id, {
    ...data.entities.albums[id],
    fetching: false
  })
}

function fetchAlbumFailure (id, error) {
  return createAction(FETCH_ALBUM_FAILURE, id, {
    fetchError: error,
    fetching: false
  })
}

function saveAlbumRequest (id, data, structure) {
  return createAction(SAVE_ALBUM_REQUEST, id, {
    ...data,
    json_data: JSON.stringify(structure),
    saving: true,
    saveError: null
  })
}

function saveAlbumSuccess (id, data) {
  return createAction(SAVE_ALBUM_SUCCESS, id, {
    ...data.entities.albums[id],
    saving: false
  })
}

function saveAlbumFailure (id, error) {
  return createAction(SAVE_ALBUM_FAILURE, id, {
    saveError: error,
    saving: false
  })
}

export function deleteAlbum (id) {
  return (dispatch, getState) => {
    const state = getState()
    const endpoint = ENDPOINT_ALBUM.replace(':id', id)
    fetch(endpoint, {
      method: 'delete',
      token: state.auth.token
    })
    dispatch({
      type: DELETE_ALBUM,
      payload: {
        entities: {
          albums: {
            [id]: undefined
          },
          // Remove album ID from any playlists
          playlists: mapValues(state.entities.playlists || {}, playlist => {
            const data = JSON.parse(playlist.json_data)
            return {
              ...playlist,
              json_data: JSON.stringify({
                ...data,
                albums: data.albums.filter(a => a.album !== id)
              })
            }
          })
        }
      },
      meta: { id }
    })
    dispatch(push('/'))
    dispatch(showBanner('Album deleted'))
  }
}

const schema = {
  records: schemas.album
}

export function fetchAlbum (id) {
  return (dispatch, getState) => {
    const endpoint = ENDPOINT_ALBUM.replace(':id', id)
    const { auth: { token } } = getState()
    dispatch(fetchAlbumRequest(id))
    return fetch(endpoint, { token, schema }, dispatch).then(data => {
      dispatch(fetchAlbumSuccess(id, data))
      return data.entities.albums[id]
    }, e => {
      dispatch(fetchAlbumFailure(id, e))
    })
  }
}

export function saveAlbum (data, structure, id = NEW_ALBUM_ID, redirect = '/album/:id') {
  return (dispatch, getState) => {
    const endpoint = id === NEW_ALBUM_ID
      ? ENDPOINT_NEW_ALBUM
      : ENDPOINT_ALBUM.replace(':id', id)

    const options = {
      method: 'post',
      token: getState().auth.token,
      schema,
      body: {
        ...data,
        json_data: JSON.stringify(structure)
      }
    }
    dispatch(saveAlbumRequest(id, data, structure))
    return fetch(endpoint, options, dispatch).then(data => {
      const id = data.result.records
      dispatch(saveAlbumSuccess(id, data))
      if (redirect) {
        dispatch(push(redirect.replace(':id', id)))
      }
      return data.entities.albums[id]
    }, e => {
      dispatch(saveAlbumFailure(id, e))
    })
  }
}

export function setFavouriteAlbum (id, favourite) {
  return (dispatch, getState) => {
    const endpoint = ENDPOINT_SET_FAVOURITE_ALBUM.replace(':id', id)
    const method = favourite ? 'post' : 'delete'
    const action = favourite ? SET_FAVOURITE_ALBUM : REMOVE_FAVOURITE_ALBUM
    const options = {
      method,
      token: getState().auth.token
    }
    dispatch({
      type: action,
      meta: { id }
    })
    fetch(endpoint, options, dispatch)
  }
}

export function setFavouriteImage (id, favourite) {
  return (dispatch, getState) => {
    const album = getFavouriteImagesAlbum(getState())
    if (!album) {
      console.warn('Image could not be favourited. No Favourite Images album was found')
      return
    }
    const structure = JSON.parse(album.json_data)
    if (favourite) {
      structure.include = structure.include.concat(id)
    } else {
      structure.include = structure.include.filter(imageID => imageID !== id)
    }
    const cover = album.cover || structure.include[structure.include.length - 1]
    dispatch(saveAlbum({ name: album.name, cover }, structure, album.id, false))
  }
}

export function addImagesToAlbum (album, images) {
  return (dispatch, getState) => {
    const structure = JSON.parse(album.json_data)
    structure.include = uniq(structure.include.concat(images))
    return dispatch(saveAlbum({ name: album.name }, structure, album.id, false))
  }
}

export function setAlbumCover (album, imageID) {
  return dispatch => {
    return dispatch(saveAlbum({
      name: album.name,
      cover: imageID
    }, JSON.parse(album.json_data), album.id, false)).then(() => {
      dispatch(showBanner('Album cover updated'))
    })
  }
}

export function shareImages (images, userID) {
  return (dispatch, getState) => {
    const endpoint = ENDPOINT_SHARE_IMAGES.replace(':user_id', userID)
    const options = {
      method: 'post',
      body: images,
      token: getState().auth.token
    }
    return fetch(endpoint, options, dispatch)
  }
}



// WEBPACK FOOTER //
// ./src/redux/album.js